import { useCallback } from 'react';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import type { ResponseModel, AnyObject } from 'common/interfaces';
import { useFetch } from 'hooks/use-fetch';

interface NotificationUpdatePayload {
  notificationIds: string[];
  isRead?: boolean;
  isNew?: boolean;
}

type UseNotificationUpdateProps<TContext = unknown> = Omit<
  UseMutationOptions<void, Error, NotificationUpdatePayload, TContext>,
  'mutationFn'
>;

export const useNotificationUpdate = <TContext = unknown>(options: UseNotificationUpdateProps<TContext> = {}) => {
  const { fetch } = useFetch<ResponseModel<AnyObject>>({
    isShowLoading: false,
    isShowErrorSnackbar: false
  });

  const updateNotifications = useCallback(
    async (payload: NotificationUpdatePayload) => {
      const { notificationIds, ...rest } = payload;

      fetch({
        requestConfig: {
          method: 'POST',
          url: '/Jobs/UpdateMessagesProps',
          data: {
            messageIds: notificationIds,
            ...rest
          }
        }
      });
    },
    [fetch]
  );

  const { mutateAsync, ...rest } = useMutation({
    mutationFn: updateNotifications,
    ...options
  });

  return [mutateAsync, rest] as const;
};
